import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import axios from "axios";
import { toast } from "sonner";
import { 
  Users, 
  Target, 
  TrendingUp,
  Leaf,
  BarChart3,
  PieChart,
  Activity,
  Shield,
  Building2,
  Award,
  MessageSquare,
  Check,
  X,
  Star
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Legend
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['hsl(140, 40%, 40%)', 'hsl(25, 60%, 55%)', 'hsl(200, 50%, 45%)'];

export default function AdminDashboard() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [sustainabilityRatio, setSustainabilityRatio] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, decisionsRes, ratioRes, testimonialsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/decisions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/sustainability-ratio`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/testimonials`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { testimonials: [] } }))
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setDecisions(decisionsRes.data);
      setSustainabilityRatio(ratioRes.data);
      setTestimonials(testimonialsRes.data.testimonials || []);
    } catch (error) {
      toast.error("Erreur lors du chargement des données admin");
    } finally {
      setLoading(false);
    }
  };

  const handleTestimonialStatus = async (testimonialId, status) => {
    try {
      await axios.put(
        `${API}/testimonials/${testimonialId}/status?status=${status}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Témoignage ${status === 'approved' ? 'approuvé' : 'rejeté'}`);
      // Refresh testimonials
      const res = await axios.get(`${API}/testimonials`, { headers: { Authorization: `Bearer ${token}` } });
      setTestimonials(res.data.testimonials || []);
    } catch (error) {
      toast.error("Erreur lors de la mise à jour du témoignage");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved': return <Badge className="bg-green-500/10 text-green-600 border-green-500/20">Approuvé</Badge>;
      case 'rejected': return <Badge className="bg-red-500/10 text-red-600 border-red-500/20">Rejeté</Badge>;
      default: return <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20">En attente</Badge>;
    }
  };

  const pendingTestimonialsCount = testimonials.filter(t => t.status === 'pending').length;

  const getDecisionTypeColor = (type) => {
    switch (type) {
      case "strategique": return "bg-chart-1 text-white";
      case "tactique": return "bg-chart-3 text-white";
      case "operationnelle": return "bg-chart-5 text-white";
      default: return "bg-muted text-muted-foreground";
    }
  };

  const getRatioTrendColor = (trend) => {
    switch (trend) {
      case "positive": return "text-chart-1";
      case "negative": return "text-destructive";
      default: return "text-chart-4";
    }
  };

  // Prepare chart data
  const decisionTypeData = stats?.decisions_by_type ? 
    Object.entries(stats.decisions_by_type).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      value
    })) : [];

  const esgData = stats?.esg_breakdown ? [
    { name: 'Environnement', value: stats.esg_breakdown.environnement || 0 },
    { name: 'Social', value: stats.esg_breakdown.social || 0 },
    { name: 'Gouvernance', value: stats.esg_breakdown.gouvernance || 0 }
  ] : [];

  if (loading) {
    return (
      <div className="min-h-screen flex">
        <Sidebar />
        <main className="flex-1 ml-[260px] p-8 bg-background">
          <div className="flex items-center justify-center h-full">
            <div className="animate-pulse-soft text-primary text-lg">Chargement...</div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <main className="flex-1 ml-[260px] p-8 bg-background" data-testid="admin-dashboard">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-6 h-6 text-primary" />
            <Badge variant="outline">Administration</Badge>
          </div>
          <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
            Tableau de bord Admin
          </h1>
          <p className="text-muted-foreground mt-1">
            Vue d'ensemble des métriques RSE et du ratio de durabilité
          </p>
        </div>

        {/* Key Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="stat-card card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-chart-5/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-chart-5" />
                </div>
              </div>
              <div className="stat-value text-foreground" data-testid="admin-total-users">
                {stats?.total_users || 0}
              </div>
              <p className="text-sm text-muted-foreground mt-1">Utilisateurs actifs</p>
            </CardContent>
          </Card>

          <Card className="stat-card card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Target className="w-6 h-6 text-primary" />
                </div>
              </div>
              <div className="stat-value text-foreground" data-testid="admin-total-decisions">
                {stats?.total_decisions || 0}
              </div>
              <p className="text-sm text-muted-foreground mt-1">Décisions analysées</p>
            </CardContent>
          </Card>

          <Card className="stat-card card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-chart-1/10 flex items-center justify-center">
                  <Leaf className="w-6 h-6 text-chart-1" />
                </div>
              </div>
              <div className="stat-value text-chart-1" data-testid="admin-avg-score">
                {stats?.avg_sustainability_score || 0}%
              </div>
              <p className="text-sm text-muted-foreground mt-1">Score ESG moyen</p>
            </CardContent>
          </Card>

          <Card className="stat-card card-hover border-2" style={{ borderColor: 'hsl(var(--chart-1))' }}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-chart-1/10 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-chart-1" />
                </div>
                <Badge className={getRatioTrendColor(sustainabilityRatio?.trend)}>
                  {sustainabilityRatio?.trend === "positive" ? "En hausse" : 
                   sustainabilityRatio?.trend === "negative" ? "En baisse" : "Stable"}
                </Badge>
              </div>
              <div className="stat-value text-chart-1" data-testid="sustainability-ratio">
                {sustainabilityRatio?.ratio || 0}%
              </div>
              <p className="text-sm text-muted-foreground mt-1">Ratio de durabilité</p>
            </CardContent>
          </Card>
        </div>

        {/* Sustainability Breakdown */}
        {sustainabilityRatio && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Activity className="w-5 h-5 text-primary" />
                Répartition des décisions par niveau de durabilité
              </CardTitle>
              <CardDescription>
                Classification basée sur le score ESG global
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { key: "excellent", label: "Excellent (≥80)", color: "bg-chart-1" },
                  { key: "bon", label: "Bon (60-79)", color: "bg-chart-2" },
                  { key: "moyen", label: "Moyen (40-59)", color: "bg-chart-4" },
                  { key: "faible", label: "Faible (<40)", color: "bg-destructive" }
                ].map((item) => (
                  <div key={item.key} className="p-4 rounded-xl bg-muted/50">
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-3 h-3 rounded-full ${item.color}`} />
                      <span className="text-sm text-muted-foreground">{item.label}</span>
                    </div>
                    <div className="text-2xl font-bold text-foreground">
                      {sustainabilityRatio.breakdown?.[item.key] || 0}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Charts & Tables */}
        <Tabs defaultValue="charts" className="space-y-6">
          <TabsList>
            <TabsTrigger value="charts" data-testid="tab-charts">
              <BarChart3 className="w-4 h-4 mr-2" />
              Graphiques
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">
              <Users className="w-4 h-4 mr-2" />
              Utilisateurs
            </TabsTrigger>
            <TabsTrigger value="decisions" data-testid="tab-decisions">
              <Target className="w-4 h-4 mr-2" />
              Décisions
            </TabsTrigger>
            <TabsTrigger value="testimonials" data-testid="tab-testimonials" className="relative">
              <MessageSquare className="w-4 h-4 mr-2" />
              Témoignages
              {pendingTestimonialsCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 text-white text-xs rounded-full flex items-center justify-center">
                  {pendingTestimonialsCount}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="charts">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Decision Types Chart */}
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Manrope' }}>Répartition par type</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    {decisionTypeData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPieChart>
                          <Pie
                            data={decisionTypeData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {decisionTypeData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        Aucune donnée disponible
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* ESG Breakdown Chart */}
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Manrope' }}>Scores ESG moyens</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    {esgData.some(d => d.value > 0) ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={esgData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
                          <YAxis domain={[0, 100]} stroke="hsl(var(--muted-foreground))" />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px'
                            }}
                          />
                          <Bar dataKey="value" fill="hsl(140, 40%, 40%)" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        Aucune donnée disponible
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Monthly Trends */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Manrope' }}>Tendance mensuelle</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[250px]">
                    {stats?.monthly_trends?.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={stats.monthly_trends}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" />
                          <YAxis stroke="hsl(var(--muted-foreground))" />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px'
                            }}
                          />
                          <Bar dataKey="decisions" fill="hsl(25, 60%, 55%)" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        Aucune donnée disponible
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope' }}>Liste des utilisateurs</CardTitle>
                <CardDescription>
                  {users.length} utilisateurs enregistrés
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nom</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Organisation</TableHead>
                      <TableHead>Rôle</TableHead>
                      <TableHead>Points</TableHead>
                      <TableHead>Niveau</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.full_name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-muted-foreground" />
                            {user.organization}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={user.role === "admin" ? "default" : "secondary"}>
                            {user.role}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Award className="w-4 h-4 text-chart-4" />
                            {user.loyalty_points}
                          </div>
                        </TableCell>
                        <TableCell>Niv. {user.level}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="decisions">
            <Card>
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope' }}>Toutes les décisions</CardTitle>
                <CardDescription>
                  {decisions.length} décisions analysées
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Titre</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Score ESG</TableHead>
                      <TableHead>Horizon</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {decisions.slice(0, 20).map((decision) => (
                      <TableRow key={decision.id}>
                        <TableCell className="font-medium max-w-[200px] truncate">
                          {decision.title}
                        </TableCell>
                        <TableCell>
                          <Badge className={getDecisionTypeColor(decision.decision_type)}>
                            {decision.decision_type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className={decision.overall_sustainability_score >= 70 ? "text-chart-1 font-bold" : 
                                          decision.overall_sustainability_score >= 50 ? "text-chart-4 font-bold" : 
                                          "text-destructive font-bold"}>
                            {decision.overall_sustainability_score}%
                          </span>
                        </TableCell>
                        <TableCell className="capitalize">{decision.timeframe}</TableCell>
                        <TableCell>
                          {new Date(decision.created_at).toLocaleDateString('fr-FR')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Testimonials Tab */}
          <TabsContent value="testimonials">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <MessageSquare className="w-5 h-5" />
                  Gestion des témoignages
                </CardTitle>
                <CardDescription>
                  {pendingTestimonialsCount > 0 
                    ? `${pendingTestimonialsCount} témoignage(s) en attente de validation`
                    : "Tous les témoignages ont été traités"
                  }
                </CardDescription>
              </CardHeader>
              <CardContent>
                {testimonials.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Aucun témoignage pour le moment</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {testimonials.map((testimonial) => (
                      <Card key={testimonial.id} className={`border ${testimonial.status === 'pending' ? 'border-amber-500/50 bg-amber-50/30' : ''}`}>
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                {getStatusBadge(testimonial.status)}
                                <div className="flex gap-0.5">
                                  {[...Array(testimonial.rating || 5)].map((_, i) => (
                                    <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />
                                  ))}
                                </div>
                                <span className="text-xs text-muted-foreground">
                                  {new Date(testimonial.created_at).toLocaleDateString('fr-FR')}
                                </span>
                              </div>
                              <p className="text-foreground italic mb-3">"{testimonial.quote}"</p>
                              <div className="flex items-center gap-2 text-sm">
                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                  <span className="text-xs font-semibold text-primary">{testimonial.author_initials}</span>
                                </div>
                                <div>
                                  <p className="font-medium text-foreground">{testimonial.author_role}</p>
                                  <p className="text-xs text-muted-foreground">{testimonial.company_description}</p>
                                </div>
                                <span className="text-xs text-muted-foreground ml-auto">
                                  par {testimonial.user_name}
                                </span>
                              </div>
                            </div>
                            {testimonial.status === 'pending' && (
                              <div className="flex flex-col gap-2">
                                <Button 
                                  size="sm" 
                                  onClick={() => handleTestimonialStatus(testimonial.id, 'approved')}
                                  className="bg-green-600 hover:bg-green-700"
                                  data-testid={`approve-testimonial-${testimonial.id}`}
                                >
                                  <Check className="w-4 h-4 mr-1" />
                                  Approuver
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleTestimonialStatus(testimonial.id, 'rejected')}
                                  className="border-red-500/50 text-red-600 hover:bg-red-50"
                                  data-testid={`reject-testimonial-${testimonial.id}`}
                                >
                                  <X className="w-4 h-4 mr-1" />
                                  Rejeter
                                </Button>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
